import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ShieldCheck, ChevronDown, ChevronRight, Plus, Trash2, Info, Lock,
  CheckCircle2, Clock, AlertTriangle, XCircle, FileText, ClipboardCheck,
  TrendingUp, Calendar, AlertCircle,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { IconButton } from "@/components/ui/icon-button";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth, hasPermission } from "@/lib/auth";
import { ComplianceEnableOverrideDialog } from "@/components/compliance-enable-override-dialog";
import { HipaaTab } from "@/components/hipaa-tab";
import { FerpaTab } from "@/components/ferpa-tab";
import { WcagTab } from "@/components/wcag-tab";

// ── Supported framework keys — only HIPAA, FERPA, and ADA/WCAG are surfaced
// in this product. All other catalog entries (PII, GLBA, GDPR, etc.) remain
// stored and enforced by the backend but are NOT shown in the UI.
const SUPPORTED_FRAMEWORK_KEYS = new Set(["hipaa", "ferpa", "ada_wcag"]);

// ── Types ─────────────────────────────────────────────────────────────────────

type FrameworkInfo = {
  key: string; name: string; description: string;
  disabledByAdmin?: boolean; restrictedByPlan?: boolean;
};
type FrameworkState = { key: string; enabled: boolean; notes?: string | null };
type ComplianceConfig = {
  frameworks: FrameworkState[];
  dataSharingRules: string[];
  piiRedactionEnabled: boolean;
  redactionCategories: string[];
  retentionDays: number;
  requireAuthForSensitive: boolean;
  consentNoticeEnabled: boolean;
  consentNoticeText?: string | null;
};

type PostureFramework = {
  key: string; name: string; description: string;
  enabled: boolean;
  lastAttestedAt: string | null;
  lastAttestedBy: string | null;
  daysSinceAttestation: number | null;
  attestationStatus: "Current" | "Due" | "Overdue" | "Never";
  openActionsCount: number;
};
type PostureResponse = {
  frameworks: PostureFramework[];
  totalOpenActionsCount: number;
};

type EvidenceRecord = {
  id: string; frameworkKey: string; evidenceType: string;
  title: string; description: string | null; sourceUrl: string | null;
  createdBy: string | null; createdAt: string;
};
type EvidenceResponse = { total: number; page: number; pageSize: number; items: EvidenceRecord[] };

type CatalogSummary = { total: number; allowed: number; restrictedByPlan: number; restrictedByAdmin: number };

// ── Settings page constants ───────────────────────────────────────────────────

const REDACTION_CATEGORIES: { value: string; label: string }[] = [
  { value: "ssn",        label: "Social Security Numbers" },
  { value: "creditcard", label: "Credit Card Numbers" },
  { value: "ein",        label: "EIN / Tax IDs" },
  { value: "email",      label: "Email Addresses" },
  { value: "phone",      label: "Phone Numbers" },
];

const EMPTY: ComplianceConfig = {
  frameworks: [], dataSharingRules: [], piiRedactionEnabled: true,
  redactionCategories: ["ssn", "creditcard", "ein"],
  retentionDays: 90, requireAuthForSensitive: false,
  consentNoticeEnabled: false, consentNoticeText: "",
};

// ── Status helpers ────────────────────────────────────────────────────────────

function attestationStatusColor(status: PostureFramework["attestationStatus"]) {
  return status === "Current"  ? "text-emerald-600 dark:text-emerald-400"
       : status === "Due"      ? "text-amber-500 dark:text-amber-400"
       : status === "Overdue"  ? "text-red-500 dark:text-red-400"
       :                         "text-muted-foreground";
}

function AttestationStatusIcon({ status }: { status: PostureFramework["attestationStatus"] }) {
  if (status === "Current")  return <CheckCircle2  className="w-4 h-4 text-emerald-500"    aria-hidden="true" />;
  if (status === "Due")      return <Clock         className="w-4 h-4 text-amber-500"       aria-hidden="true" />;
  if (status === "Overdue")  return <AlertTriangle className="w-4 h-4 text-red-500"         aria-hidden="true" />;
  return                            <XCircle       className="w-4 h-4 text-muted-foreground" aria-hidden="true" />;
}

function attestationStatusLabel(status: PostureFramework["attestationStatus"], daysAgo: number | null) {
  if (status === "Current")  return `Attested ${daysAgo === 0 ? "today" : `${daysAgo}d ago`}`;
  if (status === "Due")      return `Review due — attested ${daysAgo}d ago`;
  if (status === "Overdue")  return `Overdue — attested ${daysAgo}d ago`;
  return "Never attested";
}

function evidenceTypeIcon(type: string) {
  if (type === "Attestation")     return <ClipboardCheck className="w-3.5 h-3.5 text-primary"          aria-hidden="true" />;
  if (type === "Document")        return <FileText        className="w-3.5 h-3.5 text-blue-500"         aria-hidden="true" />;
  if (type === "SystemGenerated") return <TrendingUp      className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />;
  return                                 <FileText        className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />;
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// ── Attest dialog ─────────────────────────────────────────────────────────────

function AttestDialog({
  framework, onClose, onAttested,
}: {
  framework: PostureFramework;
  onClose: () => void;
  onAttested: () => void;
}) {
  const { toast } = useToast();
  const [notes, setNotes] = useState("");
  const attest = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/organization/compliance/attestations", {
        frameworkKey: framework.key,
        notes: notes.trim() || null,
        expiresAt: null,
      }),
    onSuccess: () => {
      toast({ title: "Attestation recorded", description: `${framework.name} marked as reviewed.` });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/posture"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/evidence"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/attestations"] });
      onAttested();
    },
    onError: (e: any) =>
      toast({ title: "Attestation failed", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Attest compliance review</DialogTitle>
          <DialogDescription>
            Confirm that you have reviewed the <strong>{framework.name}</strong> configuration
            and that it meets your organization's requirements.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">What this confirms:</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              <li>Framework settings have been reviewed and are correct</li>
              <li>Applicable policies are being followed by your organization</li>
              <li>This attestation will be logged as compliance evidence</li>
            </ul>
          </div>
          <div className="space-y-1">
            <Label htmlFor="attest-notes" className="text-xs">Notes (optional)</Label>
            <Textarea
              id="attest-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Annual HIPAA review — all controls verified by IT Admin"
              className="text-sm min-h-[80px]"
              maxLength={1000}
              data-testid="input-attest-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => attest.mutate()} disabled={attest.isPending} data-testid="button-confirm-attest">
            {attest.isPending ? "Saving…" : "Confirm attestation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Add evidence dialog ───────────────────────────────────────────────────────

function AddEvidenceDialog({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const { toast } = useToast();
  const [frameworkKey, setFrameworkKey] = useState("all");
  const [evidenceType, setEvidenceType] = useState("Document");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");

  const add = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/organization/compliance/evidence", {
        frameworkKey, evidenceType, title: title.trim(),
        description: description.trim() || null,
        sourceUrl: sourceUrl.trim() || null,
      }),
    onSuccess: () => {
      toast({ title: "Evidence record added" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/evidence"] });
      onAdded();
    },
    onError: (e: any) =>
      toast({ title: "Failed to add evidence", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add evidence record</DialogTitle>
          <DialogDescription>
            Attach an external compliance artifact (signed BAA, training certificate, audit report, etc.)
            to your evidence log.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="ev-framework">Framework</Label>
              <select id="ev-framework" className="w-full text-sm border rounded-md p-1.5 bg-background"
                value={frameworkKey} onChange={(e) => setFrameworkKey(e.target.value)}>
                <option value="all">All / General</option>
                <option value="hipaa">HIPAA</option>
                <option value="ferpa">FERPA</option>
                <option value="ada_wcag">ADA / WCAG</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="ev-type">Type</Label>
              <select id="ev-type" className="w-full text-sm border rounded-md p-1.5 bg-background"
                value={evidenceType} onChange={(e) => setEvidenceType(e.target.value)}>
                <option value="Document">Document</option>
                <option value="Training">Training record</option>
                <option value="SystemGenerated">System-generated</option>
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="ev-title">Title *</Label>
            <Input id="ev-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Signed BAA — Cloud Storage Vendor" maxLength={200} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="ev-desc">Description</Label>
            <Textarea id="ev-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of what this document covers" className="text-sm min-h-[64px]" maxLength={1000} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="ev-url">Source URL (optional)</Label>
            <Input id="ev-url" type="url" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://..." maxLength={500} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => add.mutate()} disabled={add.isPending || !title.trim()}>
            {add.isPending ? "Saving…" : "Add record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Posture tab ───────────────────────────────────────────────────────────────

function PostureTab({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [attestTarget, setAttestTarget]   = useState<PostureFramework | null>(null);
  const [showAddEvidence, setShowAddEvidence] = useState(false);

  const { data: posture, isLoading: postureLoading } = useQuery<PostureResponse>({
    queryKey: ["/api/organization/compliance/posture"],
    staleTime: 0,
  });
  const { data: evidence } = useQuery<EvidenceResponse>({
    queryKey: ["/api/organization/compliance/evidence"],
    staleTime: 0,
  });

  const deleteEvidence = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/organization/compliance/evidence/${id}`),
    onSuccess: () => {
      toast({ title: "Evidence record removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/evidence"] });
    },
    onError: (e: any) =>
      toast({ title: "Failed to remove evidence", description: e.message, variant: "destructive" }),
  });

  if (postureLoading) return <Skeleton className="h-64 w-full" />;
  if (!posture) return null;

  // Only surface the 3 supported frameworks. The backend returns all 9 but
  // unsupported ones (GDPR, GLBA, PCI-DSS, etc.) are hidden here and in Settings.
  // Show only enabled frameworks — disabled ones are not surfaced in posture.
  const visibleFrameworks = posture.frameworks.filter(
    (f) => f.enabled && SUPPORTED_FRAMEWORK_KEYS.has(f.key),
  );

  const enabledCount = visibleFrameworks.length;
  const openCount    = visibleFrameworks.reduce((s, f) => s + f.openActionsCount, 0);

  return (
    <div className="space-y-6">
      {/* Summary bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Active frameworks",  value: enabledCount, icon: ShieldCheck,    color: "text-primary" },
          { label: "Open actions",       value: openCount,    icon: AlertCircle,    color: openCount > 0 ? "text-amber-500" : "text-muted-foreground" },
          { label: "Total frameworks",   value: SUPPORTED_FRAMEWORK_KEYS.size, icon: TrendingUp, color: "text-muted-foreground" },
          {
            label: "Attestations current",
            value: visibleFrameworks.filter((f) => f.attestationStatus === "Current").length,
            icon: CheckCircle2, color: "text-emerald-500",
          },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="border rounded-md p-3 flex items-center gap-3">
            <Icon className={`w-5 h-5 shrink-0 ${color}`} aria-hidden="true" />
            <div className="min-w-0">
              <p className={`text-xl font-bold leading-none ${color}`}>{value}</p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Framework posture cards */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Framework posture</CardTitle>
          <CardDescription>
            Attestation status per enabled compliance framework. Attest after reviewing each framework's settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {visibleFrameworks.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No compliance frameworks are enabled yet.{" "}
              Go to the <strong>Settings</strong> tab to enable HIPAA, FERPA, or ADA / WCAG.
            </p>
          )}
          {visibleFrameworks.map((fw) => {
            const badgeCls = "bg-primary/10 text-primary border-primary/30";

            return (
              <div
                key={fw.key}
                className="border rounded-md p-3"
                data-testid={`posture-card-${fw.key}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <AttestationStatusIcon status={fw.attestationStatus} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium">{fw.name}</p>
                        <Badge variant="outline" className={`text-[11px] h-4 px-1.5 ${badgeCls}`}>
                          Active
                        </Badge>
                        {fw.openActionsCount > 0 && (
                          <Badge variant="outline" className="text-[11px] h-4 px-1.5 border-amber-500/30 bg-amber-500/8 text-amber-600 dark:text-amber-400">
                            {fw.openActionsCount} action{fw.openActionsCount !== 1 ? "s" : ""}
                          </Badge>
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 ${attestationStatusColor(fw.attestationStatus)}`}>
                        {attestationStatusLabel(fw.attestationStatus, fw.daysSinceAttestation)}
                        {fw.lastAttestedAt && (
                          <span className="text-muted-foreground"> · {fmtDate(fw.lastAttestedAt)}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  {canManage && (
                    <Button
                      size="sm"
                      variant={fw.attestationStatus === "Current" ? "outline" : "default"}
                      className="shrink-0 h-7 text-xs"
                      onClick={() => setAttestTarget(fw)}
                      data-testid={`button-attest-${fw.key}`}
                      aria-label={`Attest ${fw.name} compliance review`}
                    >
                      <ClipboardCheck className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                      {fw.attestationStatus === "Current" ? "Re-attest" : "Attest"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Evidence timeline */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">Evidence timeline</CardTitle>
              <CardDescription>Attestations and compliance artifacts for your organization.</CardDescription>
            </div>
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs shrink-0"
                onClick={() => setShowAddEvidence(true)}
                data-testid="button-add-evidence"
              >
                <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Add record
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!evidence || evidence.items.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" aria-hidden="true" />
              <p>No evidence records yet.</p>
              <p className="text-xs mt-1">Attest a framework above to create the first entry.</p>
            </div>
          ) : (
            <ul className="space-y-2" aria-label="Evidence timeline">
              {evidence.items.map((ev) => (
                <li
                  key={ev.id}
                  className="flex items-start gap-2 border rounded-md p-2.5"
                  data-testid={`evidence-${ev.id}`}
                >
                  <div className="mt-0.5 shrink-0">{evidenceTypeIcon(ev.evidenceType)}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-snug break-words">{ev.title}</p>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      <Badge variant="outline" className="text-[10px] h-4 px-1">
                        {ev.evidenceType}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] h-4 px-1">
                        {ev.frameworkKey === "all" ? "All frameworks" : ev.frameworkKey.toUpperCase()}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" aria-hidden="true" />
                        {fmtDate(ev.createdAt)}
                      </span>
                    </div>
                    {ev.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 break-words">{ev.description}</p>
                    )}
                    {ev.sourceUrl && (
                      <a
                        href={ev.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary underline-offset-2 hover:underline truncate block mt-0.5"
                      >
                        {ev.sourceUrl}
                      </a>
                    )}
                  </div>
                  {canManage && ev.evidenceType !== "Attestation" && (
                    <IconButton
                      variant="ghost"
                      label={`Remove evidence: ${ev.title}`}
                      onClick={() => deleteEvidence.mutate(ev.id)}
                      className="shrink-0 h-6 w-6"
                      data-testid={`button-delete-evidence-${ev.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                    </IconButton>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      {attestTarget && (
        <AttestDialog
          framework={attestTarget}
          onClose={() => setAttestTarget(null)}
          onAttested={() => setAttestTarget(null)}
        />
      )}
      {showAddEvidence && (
        <AddEvidenceDialog
          onClose={() => setShowAddEvidence(false)}
          onAdded={() => setShowAddEvidence(false)}
        />
      )}
    </div>
  );
}

// ── Settings tab (preserved exactly from the previous compliance page) ─────────

type AdaWcagDetailsProps = Record<string, never>;
function AdaWcagDetails(_: AdaWcagDetailsProps) {
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      <p className="font-medium text-foreground text-xs">What ADA / WCAG enablement does:</p>
      <ul className="list-disc list-inside space-y-0.5">
        <li>Activates automatic chat announcements for screen readers.</li>
        <li>Enables high-zoom accessibility mode for the chat widget.</li>
        <li>Adds the ADA/WCAG data-handling directive to all bot system prompts.</li>
      </ul>
      <p className="mt-1">Foundational keyboard and semantic accessibility remain on for everyone, regardless of this setting.</p>
    </div>
  );
}

function SettingsTab() {
  const { toast } = useToast();
  const { user, organization } = useAuth();
  const isSuperAdmin = (user as any)?.role === "super_admin" || (user as any)?.role === "admin";

  const { data: catalog } = useQuery<FrameworkInfo[]>({
    queryKey: ["/api/organization/compliance/catalog"],
    staleTime: 0,
  });
  const { data: catalogSummary } = useQuery<CatalogSummary>({
    queryKey: ["/api/organization/compliance/catalog/summary"],
    staleTime: 0,
  });
  const { data, isLoading, isError, refetch } = useQuery<ComplianceConfig>({
    queryKey: ["/api/organization/compliance"],
    staleTime: 0,
  });

  const [cfg, setCfg]       = useState<ComplianceConfig>(EMPTY);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [newRule, setNewRule]   = useState("");
  const [disableTarget, setDisableTarget] = useState<{ key: string; name: string } | null>(null);
  const [enableTarget,  setEnableTarget]  = useState<{ key: string; name: string } | null>(null);
  const [adminEnableTarget, setAdminEnableTarget] = useState<{
    key: string; name: string; orgId: string; orgName: string;
  } | null>(null);

  const hydratedRef = useRef(false);
  useEffect(() => {
    if (data && !hydratedRef.current) {
      hydratedRef.current = true;
      setCfg({
        ...EMPTY,
        ...data,
        // Normalise array fields: the API may return null when the org has never
        // saved settings, which would overwrite the EMPTY defaults and cause
        // .map()/.includes() to throw "not a function / not iterable".
        frameworks:          Array.isArray(data.frameworks)          ? data.frameworks          : [],
        dataSharingRules:    Array.isArray(data.dataSharingRules)    ? data.dataSharingRules    : [],
        redactionCategories: Array.isArray(data.redactionCategories) ? data.redactionCategories : EMPTY.redactionCategories,
        consentNoticeText:   data.consentNoticeText ?? "",
      });
    }
  }, [data]);

  const saveSeqRef = useRef(0);
  const save = useMutation({
    mutationFn: async (payload?: ComplianceConfig) => {
      const seq = ++saveSeqRef.current;
      const res = await apiRequest("PUT", "/api/organization/compliance", payload ?? cfg);
      return { saved: (await res.json()) as ComplianceConfig, seq };
    },
    onSuccess: ({ saved, seq }, payload) => {
      if (seq !== saveSeqRef.current) return;
      if (payload) {
        setCfg((c) => ({ ...c, frameworks: Array.isArray(saved.frameworks) ? saved.frameworks : c.frameworks }));
      } else {
        setCfg({
          ...EMPTY,
          ...saved,
          frameworks:          Array.isArray(saved.frameworks)          ? saved.frameworks          : [],
          dataSharingRules:    Array.isArray(saved.dataSharingRules)    ? saved.dataSharingRules    : [],
          redactionCategories: Array.isArray(saved.redactionCategories) ? saved.redactionCategories : EMPTY.redactionCategories,
          consentNoticeText:   saved.consentNoticeText ?? "",
        });
      }
      queryClient.setQueryData(["/api/organization/compliance"], saved);
      queryClient.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === "string" && /^\/api\/v1\/chatbots\/.+\/config$/.test(q.queryKey[0]),
      });
      // Invalidate posture so status cards refresh after a framework toggle.
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/posture"] });
      toast(
        payload
          ? { title: "Saved", description: "Framework change saved to your organization." }
          : { title: "Compliance settings saved", description: "Your bots will now follow these rules." },
      );
    },
    onError: (e: any) => {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
      hydratedRef.current = false;
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance"] });
    },
  });

  function isEnabled(key: string) {
    return cfg.frameworks.find((f) => f.key === key)?.enabled ?? false;
  }
  function toggleFramework(key: string, enabled: boolean) {
    if (!data) return;
    if (!enabled && isSuperAdmin) {
      const fw = (catalog ?? []).find((f) => f.key === key);
      setDisableTarget({ key, name: fw?.name ?? key });
      return;
    }
    const others   = cfg.frameworks.filter((f) => f.key !== key);
    const existing = cfg.frameworks.find((f) => f.key === key);
    const next: ComplianceConfig = {
      ...cfg,
      frameworks: [...others, { key, enabled, notes: existing?.notes ?? null }],
    };
    setCfg(next);
    save.mutate(next);
    if (enabled) setExpanded(key);
  }
  function proceedDisableOwnOrg(key: string) {
    if (!data) return;
    const others   = cfg.frameworks.filter((f) => f.key !== key);
    const existing = cfg.frameworks.find((f) => f.key === key);
    const next: ComplianceConfig = {
      ...cfg,
      frameworks: [...others, { key, enabled: false, notes: existing?.notes ?? null }],
    };
    setCfg(next);
    save.mutate(next);
    setDisableTarget(null);
  }
  function setNotes(key: string, notes: string) {
    setCfg((c) => {
      const others   = c.frameworks.filter((f) => f.key !== key);
      const existing = c.frameworks.find((f) => f.key === key);
      return { ...c, frameworks: [...others, { key, enabled: existing?.enabled ?? true, notes }] };
    });
  }
  function toggleCategory(value: string, on: boolean) {
    setCfg((c) => ({
      ...c,
      redactionCategories: on
        ? Array.from(new Set([...c.redactionCategories, value]))
        : c.redactionCategories.filter((v) => v !== value),
    }));
  }
  function addRule() {
    const r = newRule.trim();
    if (!r) return;
    setCfg((c) => ({ ...c, dataSharingRules: [...c.dataSharingRules, r] }));
    setNewRule("");
  }
  function removeRule(i: number) {
    setCfg((c) => ({ ...c, dataSharingRules: c.dataSharingRules.filter((_, idx) => idx !== i) }));
  }

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  if (isError || !data)
    return (
      <div className="space-y-4" data-testid="compliance-load-error">
        <p className="text-sm text-muted-foreground">
          Your saved compliance settings couldn't be loaded, so editing is disabled to protect them.
        </p>
        <Button variant="outline" onClick={() => refetch()} data-testid="button-retry-compliance">
          Try again
        </Button>
      </div>
    );

  const activeCount = cfg.frameworks.filter((f) => f.enabled).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-[14rem]">
          <p className="text-sm text-muted-foreground">
            Turn on the standards your institution must follow. Enabled frameworks add rules to each chatbot's
            AI-generated responses. Core accessibility and security remain enabled for everyone.
          </p>
        </div>
        <Button onClick={() => save.mutate(undefined)} disabled={save.isPending} data-testid="button-save-compliance">
          {save.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="rounded-md border bg-muted/30 p-3 flex items-center gap-2 text-sm" data-testid="status-active-frameworks">
        <Badge variant="default">{activeCount} active</Badge>
        <span className="text-muted-foreground">
          {activeCount === 0 ? "No frameworks enabled yet." : "These standards are applied to your bots."}
        </span>
      </div>

      {catalogSummary && catalogSummary.allowed < catalogSummary.total && (
        <div
          role="note"
          className="flex items-start gap-3 rounded-md border border-amber-500/25 bg-amber-500/8 px-4 py-3 text-sm"
          data-testid="banner-frameworks-restricted"
        >
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
          <div>
            <p className="font-medium">
              {catalogSummary.allowed} of {catalogSummary.total} compliance frameworks are available on your plan.
            </p>
            {catalogSummary.restrictedByPlan > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {catalogSummary.restrictedByPlan} framework{catalogSummary.restrictedByPlan !== 1 ? "s" : ""} not included in your current plan.
              </p>
            )}
            {catalogSummary.restrictedByAdmin > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {catalogSummary.restrictedByAdmin} framework{catalogSummary.restrictedByAdmin !== 1 ? "s" : ""} disabled by your platform administrator.
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-1">Contact your platform administrator to enable additional frameworks.</p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Compliance Frameworks</CardTitle>
          <CardDescription>Enable each standard that applies to your organization.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(catalog ?? []).filter((fw) => SUPPORTED_FRAMEWORK_KEYS.has(fw.key)).map((fw) => {
            if (fw.disabledByAdmin) {
              return (
                <div key={fw.key} className="border border-amber-500/25 rounded-md bg-amber-500/5 opacity-80"
                  data-testid={`framework-${fw.key}-locked-admin`}>
                  <div className="flex items-center justify-between p-3 gap-3">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <Lock className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" aria-hidden="true" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium break-words">{fw.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{fw.description}</p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          {isSuperAdmin
                            ? "Platform override active — disabled by an administrator."
                            : "Included in your plan but disabled by your platform administrator."}
                        </p>
                      </div>
                    </div>
                    {isSuperAdmin ? (
                      <Button size="sm" variant="outline"
                        className="shrink-0 h-7 text-xs border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                        onClick={() => setEnableTarget({ key: fw.key, name: fw.name })}
                        data-testid={`button-enable-override-${fw.key}`}
                        aria-label={`Enable ${fw.name} — remove platform override`}>
                        Enable Override
                      </Button>
                    ) : (
                      <Switch checked={false} disabled
                        aria-label={`${fw.name} — disabled by platform administrator`}
                        data-testid={`switch-${fw.key}`} />
                    )}
                  </div>
                </div>
              );
            }

            const on    = isEnabled(fw.key);
            const open  = expanded === fw.key;
            const notes = cfg.frameworks.find((f) => f.key === fw.key)?.notes ?? "";
            const panelId = `framework-panel-${fw.key}`;
            const isAda = fw.key === "ada_wcag";
            return (
              <div key={fw.key} className="border rounded-md" data-testid={`framework-${fw.key}`}>
                <div className="flex items-center justify-between p-3 gap-3">
                  <button type="button" className="flex items-start gap-2 text-left min-w-0 flex-1"
                    onClick={() => setExpanded(open ? null : fw.key)}
                    aria-expanded={open} aria-controls={panelId}
                    aria-label={isAda ? `${open ? "Hide" : "Show"} ADA/WCAG accessibility details` : `${open ? "Hide" : "Show"} ${fw.name} details`}
                    data-testid={`button-expand-${fw.key}`}>
                    {open ? <ChevronDown className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                           : <ChevronRight className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium break-words">{fw.name}</p>
                      <p className={`text-xs text-muted-foreground ${open ? "whitespace-normal break-words" : "truncate"}`}
                        data-testid={`description-${fw.key}`}>{fw.description}</p>
                    </div>
                  </button>
                  <Switch checked={on} onCheckedChange={(v) => toggleFramework(fw.key, v)}
                    aria-label={`${fw.name} — enable for this organization`}
                    data-testid={`switch-${fw.key}`} />
                </div>
                {open && (
                  <div id={panelId} className="border-t p-3 space-y-3 bg-muted/20" data-testid={`panel-${fw.key}`}>
                    <p className={`text-xs rounded-md border p-2 ${on ? "border-primary/40 bg-primary/5" : "bg-background"}`}
                      data-testid={`status-${fw.key}`}>
                      {isAda
                        ? on ? "Enabled — automatic chat announcements and high-zoom accessibility assistance are active."
                             : "Disabled — foundational keyboard and semantic accessibility remain available."
                        : on ? `Enabled — ${fw.name} rules are applied to chatbots owned by this organization.`
                             : `Disabled — ${fw.name} rules are not currently applied.`}
                    </p>
                    {isAda ? <AdaWcagDetails /> : null}
                    {on && (
                      <div className="space-y-2">
                        <Label className="text-xs" htmlFor={`input-notes-${fw.key}`}>Internal notes (optional)</Label>
                        <Input id={`input-notes-${fw.key}`} value={notes}
                          onChange={(e) => setNotes(fw.key, e.target.value)}
                          placeholder="e.g. scope, owner, review date"
                          data-testid={`input-notes-${fw.key}`} />
                        {!isAda && (
                          <p className="text-[11px] text-muted-foreground flex items-start gap-1">
                            <Info className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />
                            When enabled, the bot is instructed to enforce {fw.name} data-handling boundaries.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {isSuperAdmin && <AdminOverridesPanel onEnableOverride={(key, name, orgId, orgName) => setAdminEnableTarget({ key, name, orgId, orgName })} />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Data Privacy Controls</CardTitle>
          <CardDescription>How sensitive data is handled, retained, and disclosed.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <p className="text-sm font-medium" id="switch-pii-redaction-label">Redact PII from chat</p>
              <p className="text-xs text-muted-foreground">Detect and mask sensitive identifiers in messages.</p>
            </div>
            <Switch checked={cfg.piiRedactionEnabled}
              onCheckedChange={(v) => setCfg((c) => ({ ...c, piiRedactionEnabled: v }))}
              aria-labelledby="switch-pii-redaction-label" data-testid="switch-pii-redaction" />
          </div>
          {cfg.piiRedactionEnabled && (
            <div className="pl-1 grid sm:grid-cols-2 gap-2">
              {REDACTION_CATEGORIES.map((cat) => (
                <label key={cat.value} className="flex items-center gap-2 text-sm border rounded-md p-2 cursor-pointer"
                  data-testid={`category-${cat.value}`}>
                  <input type="checkbox" checked={cfg.redactionCategories.includes(cat.value)}
                    onChange={(e) => toggleCategory(cat.value, e.target.checked)}
                    data-testid={`checkbox-${cat.value}`} />
                  {cat.label}
                </label>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <p className="text-sm font-medium" id="switch-require-auth-label">Require sign-in for personal data</p>
              <p className="text-xs text-muted-foreground">The bot only shares record-specific info with verified users.</p>
            </div>
            <Switch checked={cfg.requireAuthForSensitive}
              onCheckedChange={(v) => setCfg((c) => ({ ...c, requireAuthForSensitive: v }))}
              aria-labelledby="switch-require-auth-label" data-testid="switch-require-auth" />
          </div>
          <div className="border rounded-md p-3 space-y-2">
            <Label className="text-xs" htmlFor="input-retention-days">Data retention period (days)</Label>
            <Input id="input-retention-days" type="number" min={0} max={3650} value={cfg.retentionDays}
              onChange={(e) => setCfg((c) => ({ ...c, retentionDays: Number(e.target.value) || 0 }))}
              className="max-w-[160px]" data-testid="input-retention-days" />
            <p className="text-[11px] text-muted-foreground">Set 0 to keep data indefinitely.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Data-Sharing Boundaries</CardTitle>
          <CardDescription>Custom rules the bot must follow on every response.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={newRule} onChange={(e) => setNewRule(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRule(); } }}
              placeholder="e.g. Never reveal another student's grades"
              aria-label="New data-sharing rule"
              data-testid="input-new-rule" />
            <Button variant="outline" onClick={addRule} data-testid="button-add-rule">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          {cfg.dataSharingRules.length === 0 ? (
            <p className="text-sm text-muted-foreground">No custom rules yet.</p>
          ) : (
            <ul className="border rounded-md divide-y">
              {cfg.dataSharingRules.map((r, i) => (
                <li key={i} className="flex items-center justify-between p-2.5 text-sm" data-testid={`rule-${i}`}>
                  <span className="min-w-0 break-words pr-2">{r}</span>
                  <IconButton variant="ghost" label={`Remove rule: ${r}`} onClick={() => removeRule(i)} data-testid={`button-remove-rule-${i}`}>
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </IconButton>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Consent Notice</CardTitle>
          <CardDescription>Shown to visitors before they start chatting.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <p className="text-sm font-medium" id="switch-consent-notice-label">Show a consent / privacy notice</p>
              <p className="text-xs text-muted-foreground">Displayed in the chat widget when enabled.</p>
            </div>
            <Switch checked={cfg.consentNoticeEnabled}
              onCheckedChange={(v) => setCfg((c) => ({ ...c, consentNoticeEnabled: v }))}
              aria-labelledby="switch-consent-notice-label" data-testid="switch-consent-notice" />
          </div>
          {cfg.consentNoticeEnabled && (
            <textarea value={cfg.consentNoticeText ?? ""}
              onChange={(e) => setCfg((c) => ({ ...c, consentNoticeText: e.target.value }))}
              className="w-full min-h-[100px] text-sm border rounded-md p-3 bg-background"
              placeholder="By using this assistant you agree to our data handling policy…"
              aria-label="Consent notice text"
              data-testid="input-consent-text" />
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => save.mutate(undefined)} disabled={save.isPending} data-testid="button-save-compliance-bottom">
          {save.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>

      {disableTarget && (
        <SuperAdminDisableForTenantDialog
          frameworkKey={disableTarget.key} frameworkName={disableTarget.name}
          onClose={() => setDisableTarget(null)}
          onDisableOwnOrg={() => proceedDisableOwnOrg(disableTarget.key)} />
      )}
      {enableTarget && (
        <ComplianceEnableOverrideDialog
          frameworkKey={enableTarget.key} frameworkName={enableTarget.name}
          orgId={(organization as any)?.id ?? ""} orgName={(organization as any)?.name ?? "your organization"}
          onClose={() => setEnableTarget(null)}
          onEnabled={() => {
            queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/catalog"] });
            queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/catalog/summary"] });
            queryClient.invalidateQueries({ queryKey: ["/api/admin/compliance-overrides/summary"] });
            setEnableTarget(null);
          }} />
      )}
      {adminEnableTarget && (
        <ComplianceEnableOverrideDialog
          frameworkKey={adminEnableTarget.key} frameworkName={adminEnableTarget.name}
          orgId={adminEnableTarget.orgId} orgName={adminEnableTarget.orgName}
          onClose={() => setAdminEnableTarget(null)}
          onEnabled={() => {
            queryClient.invalidateQueries({ queryKey: ["/api/admin/compliance-overrides/summary"] });
            queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/catalog"] });
            queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/catalog/summary"] });
            setAdminEnableTarget(null);
          }} />
      )}
    </div>
  );
}

// ── AdminOverridesPanel (preserved exactly) ───────────────────────────────────

type OverrideEntry = { orgId: string; orgName: string; key: string; name: string; disabledAt?: string; disabledByEmail?: string };
type OverrideSummary = { total: number; entries: OverrideEntry[] };

function AdminOverridesPanel({ onEnableOverride }: {
  onEnableOverride: (key: string, name: string, orgId: string, orgName: string) => void;
}) {
  const { data } = useQuery<OverrideSummary>({
    queryKey: ["/api/admin/compliance-overrides/summary"],
    staleTime: 0,
  });
  const entries = Array.isArray(data?.entries) ? data.entries : [];
  if (!data || data.total === 0 || entries.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Platform Admin Overrides</CardTitle>
        <CardDescription>Frameworks you have disabled for specific tenants.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {entries.map((e) => (
          <div key={`${e.orgId}-${e.key}`}
            className="flex items-center justify-between border rounded-md p-2.5 bg-amber-500/5 border-amber-500/20">
            <div className="min-w-0">
              <p className="text-sm font-medium">{e.name}</p>
              <p className="text-xs text-muted-foreground">{e.orgName}</p>
            </div>
            <Button size="sm" variant="outline"
              className="shrink-0 h-7 text-xs border-amber-500/40 text-amber-700 dark:text-amber-300"
              onClick={() => onEnableOverride(e.key, e.name, e.orgId, e.orgName)}>
              Enable Override
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ── SuperAdminDisableForTenantDialog (preserved exactly) ─────────────────────

function SuperAdminDisableForTenantDialog({
  frameworkKey, frameworkName, onClose, onDisableOwnOrg,
}: {
  frameworkKey: string; frameworkName: string;
  onClose: () => void; onDisableOwnOrg: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Disable {frameworkName}?</DialogTitle>
          <DialogDescription>
            As a Super Admin you can disable this framework for your own organization or a specific tenant.
            Disabling removes it from the bot's compliance prompt.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={onDisableOwnOrg}>Disable for my organization</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Root page ─────────────────────────────────────────────────────────────────

export default function CompliancePage() {
  const { user } = useAuth();
  // canManage = caller holds compliance.manage permission (OrgAdmin+ by default).
  // Super Admin bypasses all permission checks inside hasPermission().
  // Auditors hold compliance.view only and cannot attest or modify settings.
  const canManage = hasPermission(user, "compliance.manage");
  const [activeTab, setActiveTab] = useState<"posture" | "settings" | "hipaa" | "ferpa" | "wcag">("posture");

  return (
    <div className="p-6 pb-28 max-w-4xl mx-auto space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="heading-compliance">
          <ShieldCheck className="w-6 h-6" /> Compliance Center
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Monitor your compliance posture, record attestations, and configure framework settings for your organization.
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex border-b gap-1 flex-wrap" role="tablist" aria-label="Compliance Center sections">
        {(["posture", "hipaa", "ferpa", "wcag", "settings"] as const).map((tab) => (
          <button
            key={tab}
            role="tab"
            type="button"
            aria-selected={activeTab === tab}
            aria-controls={`compliance-panel-${tab}`}
            id={`compliance-tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab === "posture" ? "Posture"
              : tab === "settings" ? "Settings"
              : tab === "hipaa" ? "HIPAA"
              : tab === "ferpa" ? "FERPA"
              : "WCAG"}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      <div
        id="compliance-panel-posture"
        role="tabpanel"
        aria-labelledby="compliance-tab-posture"
        hidden={activeTab !== "posture"}
      >
        {activeTab === "posture" && <PostureTab canManage={canManage} />}
      </div>
      <div
        id="compliance-panel-settings"
        role="tabpanel"
        aria-labelledby="compliance-tab-settings"
        hidden={activeTab !== "settings"}
      >
        {activeTab === "settings" && <SettingsTab />}
      </div>
      <div
        id="compliance-panel-hipaa"
        role="tabpanel"
        aria-labelledby="compliance-tab-hipaa"
        hidden={activeTab !== "hipaa"}
      >
        {activeTab === "hipaa" && <HipaaTab canManage={canManage} />}
      </div>
      <div
        id="compliance-panel-ferpa"
        role="tabpanel"
        aria-labelledby="compliance-tab-ferpa"
        hidden={activeTab !== "ferpa"}
      >
        {activeTab === "ferpa" && <FerpaTab canManage={canManage} />}
      </div>
      <div
        id="compliance-panel-wcag"
        role="tabpanel"
        aria-labelledby="compliance-tab-wcag"
        hidden={activeTab !== "wcag"}
      >
        {activeTab === "wcag" && <WcagTab canManage={canManage} />}
      </div>
    </div>
  );
}
